import { useEffect, useRef, type RefObject } from 'react';

/**
 * Keeps a chat transcript reliably pinned to its newest content — the fix for the
 * "the agent doesn't scroll to the latest message/result" bug. A naive
 * `endRef.scrollIntoView()` on `[msgs, thinking]` fires BEFORE the tall async
 * content (ChatMarkdown + tool cards + an ActionCard flipping proposed→executed)
 * finishes laying out, so a smooth scroll to a moving target lands short. This
 * hook makes it correct with two cooperating mechanisms:
 *
 * 1. **Double-`requestAnimationFrame`** on every new turn: scroll only after the
 *    freshly-committed content has been measured, so we scroll to the real bottom.
 * 2. **`ResizeObserver`** on the transcript body: as late content grows (streaming
 *    markdown, cards expanding), re-pin to the bottom — but only when the user is
 *    already AT the tail. If they deliberately scrolled UP to read history we leave
 *    them there (near-bottom threshold), so growth never yanks the view.
 *
 * Works for both scroll models: pass `scrollRef` for an element scroller (the ⌘K
 * drawer's own `overflow-y-auto` box) or omit it to drive the window/document (the
 * /agente page, which scrolls at the window level). The page clears its sticky
 * docked composer via a `scroll-mb-*` (scroll-margin) on the `endRef` sentinel so
 * `block: 'end'` lands the newest bubble ABOVE the composer, not under it.
 */

/** Distance (px) from the bottom still treated as "the user is at the tail". */
const NEAR_BOTTOM_PX = 160;

interface StickToBottomArgs {
  /** The element whose height grows as content streams in (the transcript body). */
  contentRef: RefObject<HTMLElement | null>;
  /** An empty sentinel rendered at the very end of the transcript. */
  endRef: RefObject<HTMLElement | null>;
  /** The scrollable viewport. Omit/undefined to scroll the window/document. */
  scrollRef?: RefObject<HTMLElement | null> | undefined;
  /** Whether the transcript is currently mounted/visible (its refs are attached). */
  active: boolean;
  /** Re-pin whenever any of these change (e.g. `[msgs, thinking]`). */
  watch: readonly unknown[];
  /** True this render when the newest turn is the user's own send — always scroll,
   * even if they had scrolled up (you must see the message you just sent). */
  stickHard: boolean;
}

export function useStickToBottom({
  contentRef,
  endRef,
  scrollRef,
  active,
  watch,
  stickHard,
}: StickToBottomArgs): void {
  // Whether the viewport is currently at/near the tail. Sampled on scroll so the
  // ResizeObserver (which fires AFTER content grows, when the tail may already be
  // pushed offscreen) can decide from pre-growth intent, not stale geometry.
  const atBottom = useRef(true);
  const stickHardRef = useRef(stickHard);
  stickHardRef.current = stickHard;

  function distanceFromBottom(): number {
    const el = scrollRef?.current;
    if (el) return el.scrollHeight - el.scrollTop - el.clientHeight;
    const doc = document.documentElement;
    return doc.scrollHeight - window.scrollY - window.innerHeight;
  }

  function scrollToEnd(smooth: boolean): void {
    endRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'end' });
  }

  // Track near-bottom on the correct scroller (element or window).
  useEffect(() => {
    if (!active) return undefined;
    const target: EventTarget = scrollRef?.current ?? window;
    const onScroll = () => {
      atBottom.current = distanceFromBottom() <= NEAR_BOTTOM_PX;
    };
    onScroll();
    target.addEventListener('scroll', onScroll, { passive: true });
    return () => target.removeEventListener('scroll', onScroll);
    // Deps are the scroller identity + activation; the helpers read live refs, so
    // there is nothing stale to re-subscribe for on every render.
  }, [active, scrollRef]);

  // Pin on async content growth (markdown, tool cards, an ActionCard flip) — only
  // when the user is already at the tail, so scrolling up to read is never undone.
  useEffect(() => {
    if (!active) return undefined;
    const content = contentRef.current;
    if (!content || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      if (atBottom.current || stickHardRef.current) scrollToEnd(false);
    });
    ro.observe(content);
    return () => ro.disconnect();
    // Attach once per (re)activation; the observer callback reads live refs.
  }, [active, contentRef]);

  // On a new turn (or when the surface first opens), scroll after two frames so the
  // freshly-laid-out tall content is measured before we scroll to a now-correct target.
  useEffect(() => {
    if (!active) return undefined;
    if (!(stickHardRef.current || atBottom.current)) return undefined;
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => scrollToEnd(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      if (inner) cancelAnimationFrame(inner);
    };
    // Re-run on (re)activation and on each watched turn ([msgs, thinking]).
  }, [active, ...watch]);
}
